import ambipomSprite from '../../assets/party/stills/ambipom.png';
import chikoritaSprite from '../../assets/party/stills/chikorita.png';
import piplupSprite from '../../assets/party/stills/piplup.png';
import psyduckSprite from '../../assets/party/stills/psyduck.png';
import meowthSprite from '../../assets/party/stills/meowth.png';
import jolteonSprite from '../../assets/party/stills/jolteon.png';
import snorlaxSprite from '../../assets/party/stills/snorlax.png';
import espressoCountdown from '../../assets/projects/espresso/countdown.png';
import espressoBlitzRoom from '../../assets/projects/espresso/blitz-room.png';
import espressoRatingModal from '../../assets/projects/espresso/rating-modal.png';
import espressoProfileFlow from '../../assets/projects/espresso/profile-flow.png';
import sprintHome from '../../assets/projects/sprint/home.png';
import sprintHistory from '../../assets/projects/sprint/history.png';
import bioAttentionAbstract from '../../assets/projects/bio-attention/abstract.png';
import bioAttentionResults from '../../assets/projects/bio-attention/results.png';
import bioAttentionReport from '../../assets/projects/bio-attention/SYDE552_Final_Report.pdf';
import footballFormation from '../../assets/projects/poke-football/formation-and-scouting.png';
import footballComparison from '../../assets/projects/poke-football/roster-comparison.png';
import footballGauntlet from '../../assets/projects/poke-football/gauntlet-results.png';

export const PROJECT_PARTY = [
  {
    id: 'ambipom',
    pokemonName: 'Ambipom',
    type: 'Productivity',
    accent: '#7c70a8',
    accentSoft: '#ddd6ee',
    image: ambipomSprite,
    title: 'Sprint',
    subtitle: 'Weekly accountability tracker',
    summary: 'A weekly sprint system my buddy and I built to keep ourselves motivated.',
    description:
      'Sprint is a personal accountability app my buddy and I made so we could treat our lives more like weekly sprints. We plan a week with a fixed story point budget, log small wins across life categories, and use progress feedback to stay motivated instead of relying on vague intentions.',
    stack: ['React Native', 'TypeScript', 'Node', 'Prisma', 'Expo'],
    role: 'Product framing, frontend systems, and workflow UX.',
    impact:
      'Built the full loop from daily action to weekly reflection: task logging, user-scoped data, dynamic task creation, progress visualization, and history views that show not just output, but where time and effort actually went.',
    status: 'Prototype',
    details: {
      learned: 'How to make optimistic mobile interactions feel instant while keeping the backend as the source of truth for logs, totals, and analytics.',
      challenge: 'Keeping weekly rollups and incentive logic consistent across the app and API, especially around ISO-week boundaries and Pacific-time calculations.',
      feature: 'The segmented progress ring and pace marker. It makes the week feel like a living system instead of a spreadsheet.',
    },
    gallery: [
      {
        src: sprintHome,
        alt: 'Sprint weekly progress dashboard',
        orientation: 'portrait',
      },
      {
        src: sprintHistory,
        alt: 'Sprint log history modal',
        orientation: 'portrait',
      },
    ],
    links: [
      {
        name: 'GitHub',
        link: 'https://github.com/hassanalawie/sprint',
      },
    ],
  },
  {
    id: 'chikorita',
    pokemonName: 'Chikorita',
    type: 'Social Product',
    accent: '#668f62',
    accentSoft: '#d7e4cf',
    image: chikoritaSprite,
    title: 'Espresso',
    subtitle: 'Making networking easy',
    summary: 'An AI-powered networking platform built to make starting conversations feel easier and more natural.',
    description:
      "Espresso is a real-time networking platform designed to help people connect with more confidence. The product combines secure authentication, live communication infrastructure, and AI-generated conversation starters so users can move from profile browsing to meaningful conversation with less friction. The site is no longer live because we do not want to keep paying for a URL :')",
    stack: ['Next.js', 'TypeScript', 'AWS', 'Redis', 'WebSockets'],
    role: 'Product framing, full-stack implementation, and real-time interaction design.',
    impact:
      'Co-built the platform from the ground up, helping it win Velocity’s Best Startup Launch award and a $500 grant. Built Redis-backed WebSocket systems for real-time communication across 100+ concurrent users, implemented secure session management with OAuth and protected routes, and integrated personalized AI prompts driven by profile parsing and embedding-based relevance.',
    status: 'Prototype',
    details: {
      learned: 'How to blend real-time systems, authentication, and AI features into one product experience without making the app feel heavy or overengineered.',
      challenge: 'Keeping communication fast and reliable while also enforcing secure access controls and generating prompts that feel genuinely relevant to each user.',
      feature: 'The AI conversation starter feature, because it turned profile data into context-aware openers that made the product feel distinctly helpful instead of just functional.',
    },
    gallery: [
      {
        src: espressoCountdown,
        alt: 'Espresso blitz countdown screen',
      },
      {
        src: espressoBlitzRoom,
        alt: 'Espresso live blitz room with two participants',
      },
      {
        src: espressoRatingModal,
        alt: 'Espresso post-blitz rating modal',
      },
      {
        src: espressoProfileFlow,
        alt: 'Espresso profile creation flow',
      },
    ],
    links: [],
  },
  {
    id: 'piplup',
    pokemonName: 'Piplup',
    type: 'Research',
    accent: '#5d9aaa',
    accentSoft: '#d6e9eb',
    image: piplupSprite,
    title: 'Biologically Inspired Attention',
    subtitle: 'Final year neural attention paper',
    summary:
      'A final-year paper exploring LIF neurons and inhibitory normalization inside a tiny LLM.',
    description:
      'For my final year, I wrote a paper on a more biologically grounded alternative to standard self-attention. The project replaced the dot product in self-attention with a Leaky Integrate-and-Fire neuron population model and used divisive normalization inspired by neural inhibition to produce attention weights in a tiny language model.',
    stack: ['Python', 'PyTorch', 'Tiny LLM', 'Neural Modeling'],
    role: 'Research, implementation, experimentation, and paper writing.',
    impact:
      'The work showed how biologically inspired attention mechanisms can preserve more realistic neuron dynamics, while also surfacing the practical tradeoff: the added biological realism introduced meaningful compute overhead and reduced text generation quality.',
    status: 'Research',
    details: {
      learned:
        'Biologically plausible mechanisms can be modeled inside transformer-like systems, but the efficiency gap is a central constraint.',
      challenge:
        'Designing neuron-inspired similarity and normalization functions that remained trainable and interpretable inside a tiny LLM.',
      feature:
        'The comparison between standard attention distributions and biologically inspired score distributions across threshold and sigma experiments.',
    },
    gallery: [
      {
        src: bioAttentionAbstract,
        alt: 'Biologically Inspired Attention paper abstract page',
        orientation: 'portrait',
      },
      {
        src: bioAttentionResults,
        alt: 'Biologically Inspired Attention paper results page',
        orientation: 'portrait',
      },
    ],
    links: [
      {
        name: 'Report PDF',
        link: bioAttentionReport,
      },
    ],
  },
  {
    id: 'meowth',
    pokemonName: 'Meowth',
    type: 'Quant Research',
    accent: '#b88435',
    accentSoft: '#f0dfb4',
    image: meowthSprite,
    title: 'Nerd Sniped: Betting Equation',
    subtitle: 'Testing NFL moneyline calibration',
    summary:
      'A practical test of whether a simple betting equation could uncover a repeatable pricing error in NFL moneylines.',
    description:
      'Inspired by David Sumpter’s “Betting Equation,” I fit a two-parameter logistic model to closing NFL moneyline odds and outcomes. The experiment separated sportsbook vig from genuine probability miscalibration, tested the stability of the fitted parameters, and reserved the 2025 season as untouched validation data.',
    stack: ['Python', 'Logistic Regression', 'Bootstrapping', 'NFL Odds'],
    role: 'Research question, data preparation, model fitting, validation, and write-up.',
    impact:
      'Analyzed 1,339 games from 2020–2024. After removing vig, the fitted beta was 1.0487, but its 95% bootstrap interval of 0.901–1.209 showed no statistically robust edge, and the 2025 holdout produced no qualifying bets.',
    status: 'Complete',
    details: {
      learned:
        'The model could expose the sportsbook’s margin, but once vig was removed there was little evidence of a persistent market-pricing error.',
      challenge:
        'Separating bookmaker margin from real miscalibration and resisting the temptation to overread a small deviation from beta = 1.',
      feature:
        'The game-level bootstrap and untouched 2025 validation, because they turned a promising fitted value into an honest test of whether the signal generalized.',
    },
    links: [
      {
        name: 'GitHub',
        link: 'https://github.com/hassanalawie/BasicMoneylineLogisticRegression',
      },
    ],
  },
  {
    id: 'psyduck',
    pokemonName: 'Psyduck',
    type: 'Neural Simulation',
    accent: '#4f93aa',
    accentSoft: '#cde6eb',
    image: psyduckSprite,
    title: 'Retina to V1',
    subtitle: 'Simulating visual receptive fields',
    summary:
      'A visual simulation of how retinal receptive fields can combine into orientation-selective V1 responses.',
    description:
      'I built a RetinalResponse model to make the retinal-to-V1 pathway tangible. A movable bar of light stimulates a two-dimensional field of retinal cells modeled with center-surround responses, then simplified V1 cells combine those signals through ON/OFF receptive-field patterns.',
    stack: ['Python', 'Difference of Gaussians', 'Receptive Fields', 'V1 Modeling'],
    role: 'Literature review, neural modeling, simulation design, and implementation.',
    impact:
      'Modeled 100 retinal cells across an image and demonstrated how light position, darkness, and bar orientation change the downstream response of location- and orientation-selective V1 cells.',
    status: 'Complete',
    details: {
      learned:
        'Orientation selectivity is also spatial selectivity: a V1 cell responds to the right pattern only when it appears in the receptive-field region that cell represents.',
      challenge:
        'Translating center-surround inhibition and ON/OFF V1 receptive fields into a compact model that stays intuitive enough to visualize.',
      feature:
        'The movable light stimulus, because it makes the inhibitory surround and sharply selective V1 response visible instead of leaving them as equations on a page.',
    },
    links: [
      {
        name: 'GitHub',
        link: 'https://github.com/hassanalawie/RetinalResponse-',
      },
    ],
  },
  {
    id: 'jolteon',
    pokemonName: 'Jolteon',
    type: 'Neural Data',
    accent: '#c69b2f',
    accentSoft: '#f1e6ad',
    image: jolteonSprite,
    title: 'Spike Sorting',
    subtitle: 'Finding neurons in raw recordings',
    summary:
      'An end-to-end spike-sorting pipeline built from raw extracellular recordings and checked against a known intracellular neuron.',
    description:
      'Using the CRCNS hc-1 dataset, I decoded four-channel extracellular recordings, filtered and detected candidate spikes, aligned 32-sample waveforms, clustered their multi-electrode signatures, and evaluated the resulting putative units with physiological and statistical quality checks.',
    stack: ['Python', 'Signal Processing', 'K-Means', 'CRCNS hc-1'],
    role: 'Signal-processing pipeline, clustering, quality analysis, validation, and write-up.',
    impact:
      'Separated four waveform clusters, rejected a noisy cluster using refractory violations and L-ratio, and matched cluster 3 to the intracellular ground truth with 0.797 precision, 0.806 recall, and a 0.802 F1 score.',
    status: 'Complete',
    details: {
      learned:
        'Clustering alone is not enough for spike sorting; physiological constraints, isolation metrics, and ground truth are what turn clusters into credible putative neurons.',
      challenge:
        'Distinguishing real units from noise when a cluster can look plausible in waveform space but fail refractory-period and isolation checks.',
      feature:
        'The intracellular validation step, where the 862-spike cluster aligned with the known 852-spike neuron and consistently led it by about 0.2 ms.',
    },
    links: [
      {
        name: 'GitHub',
        link: 'https://github.com/hassanalawie/SpikeSorting',
      },
    ],
  },
  {
    id: 'snorlax',
    pokemonName: 'Snorlax',
    type: 'Game Design',
    accent: '#4d7280',
    accentSoft: '#d8e3d2',
    image: snorlaxSprite,
    title: 'Poké Football Draft Lab',
    subtitle: 'Drafting Pokémon into an NFL offense',
    eyebrow: 'Pokémon roster-building game',
    summary:
      'A fast browser game about scouting Pokémon, building an offense, and surviving the Kanto gym gauntlet.',
    description:
      'Poké Football Draft Lab turns Pokémon into football prospects. Players encounter final-stage Pokémon, inspect their stats and physical profile, and decide where they belong on an eleven-player offensive formation. A custom fit model translates attributes, height, weight, and typing into position grades, while items, regional and type chemistry, shinies, and legendary status create unusual roster-building decisions. Once the lineup is complete, the team runs through all eight Kanto gyms. Roster quality matters most, but chemistry and type matchups can swing close games.',
    stack: ['Next.js', 'TypeScript', 'PokéAPI', 'Netlify'],
    role:
      'Game concept, scoring model, interface design, frontend implementation, simulation-driven balance testing, and deployment.',
    impact:
      'Built the complete loop from random encounter to final gauntlet: region filtering, final-evolution validation, stat and body scouting, position grades, a visual offensive formation, roster comparisons, chemistry bonuses, held items, rare X-Factors, and eight type-aware gym matchups.',
    status: 'Complete',
    details: {
      learned:
        'A game model works best when players can understand its logic without being handed the answer. The interesting decisions appeared when the grading system was transparent enough to learn, but uncertain enough to debate.',
      challenge:
        'Making the position model understandable without making every decision obvious. Each role needed a distinct identity—and believable physical requirements—while random encounters, limited skips, items, and position switching still had to leave room for improvisation.',
      feature:
        'The football translations inside the scouting model: Special Attack becomes a quarterback’s arm, Defense becomes pass protection, and Speed creates separation for wide receivers.',
    },
    gallery: [
      {
        src: footballFormation,
        alt: 'Live offensive formation with a prospect scouting dossier',
        caption: 'Live offensive formation and prospect scouting dossier',
      },
      {
        src: footballComparison,
        alt: 'Direct player comparison during roster reassignment',
        caption: 'Direct player comparison during roster reassignment',
      },
      {
        src: footballGauntlet,
        alt: 'Kanto gym gauntlet results with matchup breakdowns',
        caption: 'Kanto gym gauntlet results and matchup breakdown',
      },
    ],
    links: [
      {
        name: 'Play',
        link: 'https://poke-football.netlify.app/',
      },
      {
        name: 'GitHub',
        link: 'https://github.com/hassanalawie/PokeFootball',
      },
    ],
  },
];
